import { Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { LayerData } from '@/atoms/layers.atom';
import {
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '@/components/ui/context-menu';
import * as Dropdown from '@/components/ui/dropdown-menu';
import { ANALYSES, MENU_GROUPS } from '@/lib/analyses/registry';
import { useAnalysisAvailability } from '@/lib/analyses/useAnalysisAvailability';
import { useLaunchAnalysis } from '@/lib/analyses/useLaunchAnalysis';
import { cn } from '@/lib/utils';

const ICON_CLASS = 'size-4 stroke-2 text-slate-700 dark:text-stone-100';

export interface LayerAnalysisSubmenusProps {
  layer: LayerData;
  /** The layer is still processing — nothing can be started from it yet. */
  disabled?: boolean;
  dropdown?: boolean;
}

/**
 * The layer context menu's Analyse ▸ / Generate ▸ / Export ▸ submenus
 * (analysis-launcher spec D1), derived entirely from the registry: an item
 * shows only if `useAnalysisAvailability` says it is visible for THIS layer.
 *
 * Locked (subscription) items stay clickable — muted + lock icon, and the
 * click reaches `useLaunchAnalysis`'s gate check, which opens the upgrade
 * dialog. Same rule as `ToggleIconButton`'s `locked` prop in Viewer.tsx: the
 * `disabled` prop would swallow that click. Only a missing project
 * prerequisite (D6.3) or a still-processing layer disables an item.
 */
export function LayerAnalysisSubmenus({ layer, disabled = false, dropdown = false }: LayerAnalysisSubmenusProps) {
  const MenuItem = dropdown ? Dropdown.DropdownMenuItem : ContextMenuItem;
  const MenuSeparator = dropdown ? Dropdown.DropdownMenuSeparator : ContextMenuSeparator;
  const MenuSub = dropdown ? Dropdown.DropdownMenuSub : ContextMenuSub;
  const MenuSubContent = dropdown ? Dropdown.DropdownMenuSubContent : ContextMenuSubContent;
  const MenuSubTrigger = dropdown ? Dropdown.DropdownMenuSubTrigger : ContextMenuSubTrigger;
  const { t: translate } = useTranslation('project');
  // Registry keys are plain strings (not literal-typed), so widen `t` once here.
  const t = translate as (key: string) => string;
  const availabilityOf = useAnalysisAvailability();
  const launch = useLaunchAnalysis();

  const groups = MENU_GROUPS.map((group) => ({
    group,
    entries: ANALYSES.filter((d) => d.menuGroup === group.id)
      .map((descriptor) => ({ descriptor, availability: availabilityOf(descriptor.id, layer) }))
      .filter((entry) => entry.availability.visible),
  })).filter((g) => g.entries.length > 0);

  if (groups.length === 0) return null;

  return (
    <>
      <MenuSeparator />
      {groups.map(({ group, entries }) => {
        const GroupIcon = group.icon;
        return (
          <MenuSub key={group.id}>
            <MenuSubTrigger data-testid={`layer-analysis-group-${group.id}`}>
              <GroupIcon className={ICON_CLASS} />
              {t(group.labelKey)}
            </MenuSubTrigger>
            <MenuSubContent className='min-w-56'>
              {entries.map(({ descriptor, availability }) => {
                const Icon = descriptor.icon;
                const blockedReasonKey = availability.blockedReasonKey;
                const inert = disabled || blockedReasonKey !== null;
                return (
                  <MenuItem
                    key={descriptor.id}
                    data-testid={`layer-analysis-item-${descriptor.id}`}
                    data-locked={availability.locked ? 'true' : undefined}
                    disabled={inert}
                    className={cn(availability.locked && 'opacity-60')}
                    onClick={() => {
                      if (inert) return;
                      launch(descriptor.id, { contextLayer: layer });
                    }}>
                    <Icon className={ICON_CLASS} />
                    <span className='flex flex-1 flex-col'>
                      <span>{t(descriptor.menuLabelKey)}</span>
                      {blockedReasonKey !== null && (
                        <span className='text-xs text-slate-500 dark:text-stone-400'>{t(blockedReasonKey)}</span>
                      )}
                    </span>
                    {availability.locked && (
                      <Lock
                        aria-label={t('analysisLauncher.locked')}
                        className='ml-auto size-3.5 text-amber-600 dark:text-amber-400'
                      />
                    )}
                  </MenuItem>
                );
              })}
            </MenuSubContent>
          </MenuSub>
        );
      })}
    </>
  );
}
